import { type CivilStatus, CivilStatusLabel } from '@idc-forms/types/civil-status'
import { type Gender, GenderLabel } from '@idc-forms/types/gender'
import { isEmpty } from 'radash'
import { buildName } from '@/shared/utils/handleData'
import type { RenapApplicant } from '../services/renap.services'

interface RenapPreviewCardProps {
  applicant: RenapApplicant
  photo?: string
}

/**
 * Presentational card shown after a successful RENAP lookup: photo + full name
 * and the key identity data the advisor checks at a glance. Reused both as the
 * toast content and inline (e.g. inside the create-client side panel). Kept out
 * of the hook so the data flow and the presentation stay separate.
 */
export function RenapPreviewCard({ applicant, photo }: RenapPreviewCardProps) {
  const fullName = buildName(
    {
      first: applicant.name?.first ?? '',
      second: applicant.name?.second,
      others: applicant.name?.others
    },
    {
      first: applicant.lastName?.first ?? '',
      second: applicant.lastName?.second,
      married: applicant.lastName?.married
    }
  )

  const genderLabel = applicant.gender ? (GenderLabel[applicant.gender as Gender] ?? '') : ''
  const civilStatusLabel = applicant.civilStatus
    ? (CivilStatusLabel[applicant.civilStatus as CivilStatus] ?? '')
    : ''

  return (
    <div className="flex gap-3">
      <img
        alt=""
        className="h-[60px] w-[50px] aspect-square object-cover object-top rounded-md"
        src={isEmpty(photo) ? '/images/user-empty.jpg' : `data:image/png;base64, ${photo}`}
      />
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-neutral-700">{fullName}</span>
        <div className="flex gap-4 text-[0.65rem] text-neutral-500">
          {applicant.birthdate && (
            <div className="flex flex-col">
              <span>Fecha de nacimiento</span>
              <span className="text-neutral-700">{applicant.birthdate}</span>
            </div>
          )}
          {genderLabel && (
            <div className="flex flex-col">
              <span>Género</span>
              <span className="text-neutral-700 uppercase">{genderLabel}</span>
            </div>
          )}
          {civilStatusLabel && (
            <div className="flex flex-col">
              <span>Estado civil</span>
              <span className="text-neutral-700 uppercase">{civilStatusLabel}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
